import React, { useState, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  Badge,
  Checkbox,
  VStack,
  Spinner,
  Progress,
  Button,
  IconButton,
  Input,
  Select,
  Tooltip,
  Collapse,
  Link,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  PencilSimple,
  Trash,
  Check,
  X,
  FloppyDisk,
  Paperclip,
  DownloadSimple,
  File as FileIcon,
} from 'phosphor-react';
import { toast } from 'sonner';
import { useEventos, IEventoChecklistItem } from '../../../../hooks/useEventos';
import { IDataNoPagination } from '../../../../types/shared';

const RESPONSAVEL_CONFIG: Record<string, { color: string; label: string }> = {
  juridico: { color: 'purple', label: 'JURÍDICO' },
  cliente: { color: 'orange', label: 'CLIENTE' },
  contabilidade: { color: 'cyan', label: 'CONTAB.' },
  producao: { color: 'pink', label: 'PRODUÇÃO' },
};

const RESPONSAVEL_OPTIONS = [
  { value: 'juridico', label: 'Jurídico' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'contabilidade', label: 'Contabilidade' },
  { value: 'producao', label: 'Produção' },
];

// ── Status Switch Component ──

function StatusSwitch({
  status,
  onChange,
  isDisabled,
}: {
  status: 'pendente' | 'concluido' | 'rejeitado';
  // eslint-disable-next-line no-unused-vars
  onChange: (newStatus: 'pendente' | 'concluido' | 'rejeitado') => void;
  isDisabled?: boolean;
}) {
  const handleClick = (target: 'rejeitado' | 'concluido') => {
    if (isDisabled) return;
    // Se já está neste estado, volta para pendente
    onChange(status === target ? 'pendente' : target);
  };

  return (
    <Flex
      borderRadius="full"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      h="24px"
      flexShrink={0}
    >
      <Flex
        w="28px"
        h="100%"
        align="center"
        justify="center"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        bg={status === 'rejeitado' ? 'red.500' : 'white'}
        onClick={() => handleClick('rejeitado')}
        transition="all 0.15s"
        _hover={status !== 'rejeitado' && !isDisabled ? { bg: 'red.50' } : undefined}
      >
        <X size={12} weight="bold" color={status === 'rejeitado' ? 'white' : '#CBD5E0'} />
      </Flex>
      <Flex
        w="28px"
        h="100%"
        align="center"
        justify="center"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        bg={status === 'concluido' ? 'green.500' : 'white'}
        borderLeft="1px solid"
        borderColor="gray.200"
        onClick={() => handleClick('concluido')}
        transition="all 0.15s"
        _hover={status !== 'concluido' && !isDisabled ? { bg: 'green.50' } : undefined}
      >
        <Check size={12} weight="bold" color={status === 'concluido' ? 'white' : '#CBD5E0'} />
      </Flex>
    </Flex>
  );
}

// ── Row styles by status ──

const STATUS_STYLES: Record<
  string,
  { bg: string; border: string; textColor: string; textDecoration: string; hoverBg: string }
> = {
  pendente: {
    bg: 'white',
    border: 'gray.100',
    textColor: 'gray.700',
    textDecoration: 'none',
    hoverBg: 'gray.50',
  },
  concluido: {
    bg: 'green.50',
    border: 'green.100',
    textColor: 'green.600',
    textDecoration: 'none',
    hoverBg: 'green.50',
  },
  rejeitado: {
    bg: 'red.50',
    border: 'red.100',
    textColor: 'red.400',
    textDecoration: 'line-through',
    hoverBg: 'red.50',
  },
};

interface ChecklistTabProps {
  eventoId: number;
}

export default function ChecklistTab({ eventoId }: ChecklistTabProps) {
  const queryClient = useQueryClient();
  const {
    getChecklist,
    setChecklistStatus,
    createChecklistItem,
    updateChecklistItem,
    deleteChecklistItem,
    createChecklistTemplateGrupoFromEvento,
    uploadChecklistAnexo,
    deleteChecklistAnexo,
  } = useEventos();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedAnexos, setExpandedAnexos] = useState<number | null>(null);
  const [uploadingItemId, setUploadingItemId] = useState<number | null>(null);

  // Estado para salvar como template
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateNome, setTemplateNome] = useState('');

  // Estado para adicionar item
  const [showAdd, setShowAdd] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newResp, setNewResp] = useState('contabilidade');
  const [newObrig, setNewObrig] = useState(false);

  // Estado para editar item
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [editResp, setEditResp] = useState('contabilidade');
  const [editObrig, setEditObrig] = useState(false);

  const { data: checklistData, isFetching } = useQuery<IDataNoPagination<IEventoChecklistItem[]>>({
    queryKey: ['evento-checklist', eventoId],
    queryFn: getChecklist,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['evento-checklist', eventoId] });
    queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
    queryClient.invalidateQueries({ queryKey: ['eventos'] });
  };

  const checklistKey = ['evento-checklist', eventoId];

  const statusMut = useMutation({
    mutationFn: setChecklistStatus,
    onMutate: async ({ itemId, status: newStatus }) => {
      await queryClient.cancelQueries({ queryKey: checklistKey });
      const previous =
        queryClient.getQueryData<IDataNoPagination<IEventoChecklistItem[]>>(checklistKey);
      queryClient.setQueryData<IDataNoPagination<IEventoChecklistItem[]>>(checklistKey, old => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map(item =>
            item.id === itemId
              ? {
                  ...item,
                  status: newStatus as IEventoChecklistItem['status'],
                  concluido: newStatus === 'concluido',
                }
              : item,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(checklistKey, context.previous);
      toast.error('Erro ao atualizar checklist');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
    },
  });

  const createMut = useMutation({
    mutationFn: createChecklistItem,
    onSuccess: () => {
      invalidate();
      toast.success('Item adicionado');
      setNewDesc('');
      setNewResp('contabilidade');
      setNewObrig(false);
      setShowAdd(false);
    },
    onError: () => toast.error('Erro ao adicionar item'),
  });

  const updateMut = useMutation({
    mutationFn: updateChecklistItem,
    onSuccess: () => {
      invalidate();
      toast.success('Item atualizado');
      setEditingId(null);
    },
    onError: () => toast.error('Erro ao atualizar item'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteChecklistItem,
    onSuccess: () => {
      invalidate();
      toast.success('Item removido');
    },
    onError: () => toast.error('Erro ao remover item'),
  });

  const uploadAnexoMut = useMutation({
    mutationFn: ({ itemId, file }: { itemId: number; file: File }) =>
      uploadChecklistAnexo({ eventoId, itemId, file }),
    onSuccess: () => {
      invalidate();
      toast.success('Anexo enviado');
      setUploadingItemId(null);
    },
    onError: () => {
      toast.error('Erro ao enviar anexo');
      setUploadingItemId(null);
    },
  });

  const deleteAnexoMut = useMutation({
    mutationFn: deleteChecklistAnexo,
    onSuccess: () => {
      invalidate();
      toast.success('Anexo removido');
    },
    onError: () => toast.error('Erro ao remover anexo'),
  });

  const saveTemplateMut = useMutation({
    mutationFn: createChecklistTemplateGrupoFromEvento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evento-checklist-template-grupos'] });
      toast.success('Template criado a partir do checklist!');
      setShowSaveTemplate(false);
      setTemplateNome('');
    },
    onError: () => toast.error('Erro ao salvar como template'),
  });

  const items = checklistData?.data ?? [];
  const total = items.length;
  const concluidos = items.filter(i => i.status === 'concluido').length;
  const rejeitados = items.filter(i => i.status === 'rejeitado').length;
  const progress = total > 0 ? (concluidos / total) * 100 : 0;

  const handleCreate = () => {
    if (!newDesc.trim()) return;
    createMut.mutate({
      eventoId,
      data: {
        descricao: newDesc.trim(),
        responsavel_tipo: newResp,
        obrigatorio: newObrig,
        ordem: items.length + 1,
      },
    });
  };

  const handleUpdate = () => {
    if (!editingId || !editDesc.trim()) return;
    updateMut.mutate({
      eventoId,
      itemId: editingId,
      data: {
        descricao: editDesc.trim(),
        responsavel_tipo: editResp,
        obrigatorio: editObrig,
      },
    });
  };

  const startEdit = (item: IEventoChecklistItem) => {
    setEditingId(item.id);
    setEditDesc(item.descricao);
    setEditResp(item.responsavel_tipo);
    setEditObrig(item.obrigatorio);
  };

  if (isFetching) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <Text fontSize="xs" fontWeight="600" color="gray.500">
            {concluidos}/{total}
          </Text>
          {rejeitados > 0 && (
            <Badge fontSize="2xs" colorScheme="red" variant="subtle" borderRadius="full">
              {rejeitados} rejeitado{rejeitados !== 1 ? 's' : ''}
            </Badge>
          )}
        </HStack>
        <HStack spacing={1}>
          {total > 0 && (
            <Tooltip label="Salvar checklist como template" fontSize="xs">
              <Button
                size="xs"
                variant="ghost"
                color="gray.400"
                fontWeight="500"
                leftIcon={<FloppyDisk size={12} weight="bold" />}
                onClick={() => setShowSaveTemplate(true)}
                borderRadius="full"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
                isDisabled={showSaveTemplate}
              >
                Salvar como template
              </Button>
            </Tooltip>
          )}
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            fontWeight="500"
            leftIcon={<Plus size={12} weight="bold" />}
            onClick={() => setShowAdd(true)}
            borderRadius="full"
            _hover={{ bg: 'gray.100', color: 'gray.700' }}
            isDisabled={showAdd}
          >
            Adicionar
          </Button>
        </HStack>
      </Flex>

      {showSaveTemplate && (
        <Box mb={3} p={3} borderRadius="md" border="1px dashed" borderColor="blue.200" bg="blue.50">
          <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
            Salvar checklist como template reutilizável
          </Text>
          <Flex gap={2}>
            <Input
              size="sm"
              borderRadius="8px"
              placeholder="Nome do template"
              value={templateNome}
              onChange={e => setTemplateNome(e.target.value)}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && templateNome.trim())
                  saveTemplateMut.mutate({ eventoId, nome: templateNome.trim() });
                if (e.key === 'Escape') {
                  setShowSaveTemplate(false);
                  setTemplateNome('');
                }
              }}
            />
            <Button
              size="sm"
              bg="blue.500"
              color="white"
              borderRadius="full"
              onClick={() => {
                if (templateNome.trim())
                  saveTemplateMut.mutate({ eventoId, nome: templateNome.trim() });
              }}
              isLoading={saveTemplateMut.isPending}
              isDisabled={!templateNome.trim()}
              _hover={{ bg: 'blue.600' }}
              px={4}
            >
              Salvar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              borderRadius="full"
              onClick={() => {
                setShowSaveTemplate(false);
                setTemplateNome('');
              }}
            >
              <X size={14} />
            </Button>
          </Flex>
        </Box>
      )}
      <Progress
        value={progress}
        size="sm"
        borderRadius="full"
        colorScheme={progress === 100 ? 'green' : 'blue'}
        mb={4}
      />

      <VStack spacing={1} align="stretch">
        {items.map(item => {
          const resp = RESPONSAVEL_CONFIG[item.responsavel_tipo];
          const isEditing = editingId === item.id;
          const st = STATUS_STYLES[item.status] || STATUS_STYLES.pendente;

          if (isEditing) {
            return (
              <Box
                key={item.id}
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
                bg="blue.50"
              >
                <VStack spacing={2} align="stretch">
                  <Input
                    size="sm"
                    borderRadius="8px"
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleUpdate();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                  <HStack spacing={2}>
                    <Select
                      size="xs"
                      borderRadius="8px"
                      value={editResp}
                      onChange={e => setEditResp(e.target.value)}
                      flex={1}
                    >
                      {RESPONSAVEL_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <Checkbox
                      size="sm"
                      colorScheme="red"
                      isChecked={editObrig}
                      onChange={e => setEditObrig(e.target.checked)}
                    >
                      <Text fontSize="xs">Obrigatório</Text>
                    </Checkbox>
                  </HStack>
                  <HStack spacing={1} justify="flex-end">
                    <Button
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      onClick={() => setEditingId(null)}
                    >
                      <X size={14} />
                    </Button>
                    <Button
                      size="xs"
                      bg="blue.500"
                      color="white"
                      borderRadius="full"
                      onClick={handleUpdate}
                      isLoading={updateMut.isPending}
                      _hover={{ bg: 'blue.600' }}
                    >
                      <Check size={14} />
                      <Text ml={1}>Salvar</Text>
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            );
          }

          const anexos = item.anexos || [];
          const isExpanded = expandedAnexos === item.id;

          return (
            <Box key={item.id}>
              <Flex
                align="center"
                justify="space-between"
                py={2}
                px={3}
                borderRadius="md"
                bg={st.bg}
                border="1px solid"
                borderColor={st.border}
                _hover={{ bg: st.hoverBg }}
                role="group"
              >
                <HStack spacing={3}>
                  <StatusSwitch
                    status={item.status}
                    onChange={newStatus =>
                      statusMut.mutate({ eventoId, itemId: item.id, status: newStatus })
                    }
                    isDisabled={statusMut.isPending}
                  />
                  <Text fontSize="sm" color={st.textColor} textDecoration={st.textDecoration}>
                    {item.descricao}
                  </Text>
                </HStack>
                <HStack spacing={1.5}>
                  {item.obrigatorio && (
                    <Badge fontSize="2xs" colorScheme="red" variant="subtle">
                      OBR
                    </Badge>
                  )}
                  {resp && (
                    <Badge fontSize="2xs" colorScheme={resp.color} variant="outline">
                      {resp.label}
                    </Badge>
                  )}
                  <Tooltip
                    label={`Anexos${anexos.length ? ` (${anexos.length})` : ''}`}
                    fontSize="xs"
                  >
                    <IconButton
                      aria-label="Anexos"
                      icon={<Paperclip size={13} />}
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      color={anexos.length > 0 ? 'blue.500' : 'gray.400'}
                      _hover={{ color: 'blue.500', bg: 'blue.50' }}
                      onClick={() => setExpandedAnexos(isExpanded ? null : item.id)}
                    />
                  </Tooltip>
                  {anexos.length > 0 && (
                    <Badge fontSize="2xs" colorScheme="blue" variant="subtle" borderRadius="full">
                      {anexos.length}
                    </Badge>
                  )}
                  <HStack
                    spacing={0}
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.15s"
                  >
                    <Tooltip label="Editar" fontSize="xs">
                      <IconButton
                        aria-label="Editar"
                        icon={<PencilSimple size={12} />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        color="gray.400"
                        _hover={{ color: 'blue.500', bg: 'blue.50' }}
                        onClick={() => startEdit(item)}
                      />
                    </Tooltip>
                    <Tooltip label="Remover" fontSize="xs">
                      <IconButton
                        aria-label="Remover"
                        icon={<Trash size={12} />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        color="gray.400"
                        _hover={{ color: 'red.500', bg: 'red.50' }}
                        onClick={() => deleteMut.mutate({ eventoId, itemId: item.id })}
                        isLoading={deleteMut.isPending}
                      />
                    </Tooltip>
                  </HStack>
                </HStack>
              </Flex>

              {/* Area de anexos */}
              <Collapse in={isExpanded} animateOpacity>
                <Box ml={10} mt={1} mb={2} p={2} bg="gray.50" borderRadius="md" fontSize="xs">
                  {anexos.map(a => (
                    <Flex key={a.id} align="center" justify="space-between" py={1}>
                      <HStack spacing={2}>
                        <FileIcon size={12} color="#718096" />
                        <Link
                          href={a.url}
                          isExternal
                          color="blue.500"
                          _hover={{ textDecoration: 'underline' }}
                          fontSize="xs"
                        >
                          {a.nome}
                        </Link>
                        {a.tamanho && (
                          <Text color="gray.400">{(a.tamanho / 1024).toFixed(0)} KB</Text>
                        )}
                      </HStack>
                      <HStack spacing={1}>
                        <Tooltip label="Baixar" fontSize="xs">
                          <IconButton
                            as="a"
                            href={a.url}
                            target="_blank"
                            aria-label="Baixar"
                            icon={<DownloadSimple size={12} />}
                            size="xs"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'blue.500' }}
                          />
                        </Tooltip>
                        <Tooltip label="Remover" fontSize="xs">
                          <IconButton
                            aria-label="Remover anexo"
                            icon={<Trash size={12} />}
                            size="xs"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'red.500' }}
                            onClick={() => deleteAnexoMut.mutate(a.id)}
                            isLoading={deleteAnexoMut.isPending}
                          />
                        </Tooltip>
                      </HStack>
                    </Flex>
                  ))}
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<Plus size={10} />}
                    mt={1}
                    isLoading={uploadAnexoMut.isPending && uploadingItemId === item.id}
                    onClick={() => {
                      setUploadingItemId(item.id);
                      fileInputRef.current?.click();
                    }}
                  >
                    Anexar arquivo
                  </Button>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </VStack>

      {/* Formulário de adicionar */}
      {showAdd && (
        <Box
          mt={2}
          p={3}
          borderRadius="md"
          border="1px dashed"
          borderColor="green.200"
          bg="green.50"
        >
          <VStack spacing={2} align="stretch">
            <Input
              size="sm"
              borderRadius="8px"
              placeholder="Descrição do item"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') setShowAdd(false);
              }}
            />
            <HStack spacing={2}>
              <Select
                size="xs"
                borderRadius="8px"
                value={newResp}
                onChange={e => setNewResp(e.target.value)}
                flex={1}
              >
                {RESPONSAVEL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Checkbox
                size="sm"
                colorScheme="red"
                isChecked={newObrig}
                onChange={e => setNewObrig(e.target.checked)}
              >
                <Text fontSize="xs">Obrigatório</Text>
              </Checkbox>
            </HStack>
            <HStack spacing={1} justify="flex-end">
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                onClick={() => setShowAdd(false)}
              >
                Cancelar
              </Button>
              <Button
                size="xs"
                bg="green.500"
                color="white"
                borderRadius="full"
                onClick={handleCreate}
                isLoading={createMut.isPending}
                isDisabled={!newDesc.trim()}
                _hover={{ bg: 'green.600' }}
              >
                <Plus size={12} weight="bold" />
                <Text ml={1}>Adicionar</Text>
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {items.length === 0 && !showAdd && (
        <Flex direction="column" align="center" py={8} gap={2}>
          <Text color="gray.400" fontSize="sm">
            Nenhum item no checklist
          </Text>
          <Button
            size="xs"
            variant="outline"
            borderRadius="full"
            color="gray.500"
            leftIcon={<Plus size={12} weight="bold" />}
            onClick={() => setShowAdd(true)}
            _hover={{ bg: 'gray.50' }}
          >
            Adicionar item
          </Button>
        </Flex>
      )}
      {/* Input file hidden para upload de anexo */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file && uploadingItemId) {
            uploadAnexoMut.mutate({ itemId: uploadingItemId, file });
          }
          e.target.value = '';
        }}
      />
    </Box>
  );
}
